import { useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import styled from 'styled-components';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import ConnectTicketDropdown from 'shared/components/ConnectTicketDropdown';
import IncidentTicketCreateDialog from '../../../components/incidentTickets/IncidentTicketCreateDialog';
import { isIncidentReadOnly } from '../../utils/incidentState';
import IncidentV2TicketList from '../../components/IncidentV2TicketList';
import UnlinkIncidentV2TicketDialog from '../../components/UnlinkIncidentV2TicketDialog';
import useIncidentV2Tickets from '../../hooks/useIncidentV2Tickets';
import useIncidentV2Ticketing from '../../hooks/useIncidentV2Ticketing';
import type { IncidentDetail, IncidentV2TicketRow } from '../../types';

const Section = styled(FlexColumn)`
  gap: 8px;
`;

type Props = {
  incident: IncidentDetail;
};

const IncidentV2Tickets = ({ incident }: Props) => {
  const isReadOnly = isIncidentReadOnly(incident);
  const [ticketToUnlink, setTicketToUnlink] =
    useState<IncidentV2TicketRow | null>(null);
  const { data: tickets = [], isLoading } = useIncidentV2Tickets({
    incidentId: incident.id,
  });
  const {
    selectedEnvId,
    enabledIntegrations,
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
    incidentUrl,
    isResolved,
    attachTicket,
    isAttaching,
    unlinkTicket,
    isUnlinking,
  } = useIncidentV2Ticketing({
    incidentId: incident.id,
    status: incident.status,
  });

  const hasTicketRows = tickets.length > 0;
  const canUnlinkTicket = !isReadOnly && !isResolved;
  const canManageTickets =
    !isReadOnly &&
    !isResolved &&
    (enabledIntegrations?.length ?? 0) > 0 &&
    !!selectedEnvId;
  const shouldShowCreateTicketDialog =
    ticketDialogOpen && !!selectedIntegration && !!selectedEnvId;

  if (!hasTicketRows && !canManageTickets && !isLoading) {
    return null;
  }

  return (
    <Section>
      <Flex alignItems="center" justifyContent="space-between" gap={2}>
        <Typography variant="subtitle2" fontWeight={600}>
          Tickets
        </Typography>
        {isLoading && <CircularProgress size={16} />}
      </Flex>

      <Flex gap={1} flexWrap="wrap">
        <IncidentV2TicketList
          tickets={tickets}
          onUnlink={canUnlinkTicket ? setTicketToUnlink : undefined}
        />
        {canManageTickets && selectedEnvId && enabledIntegrations ? (
          <ConnectTicketDropdown
            envId={selectedEnvId}
            enabledIntegrations={enabledIntegrations}
            isAttaching={isAttaching}
            isCreatingTicket={isCreatingTicket}
            onSelect={async (integration, ticket) => {
              await attachTicket({
                ticketingIntegrationId: integration.id,
                ticket,
              });
            }}
            onCreateClick={(integration) => onCreateTicketClick(integration.id)}
          />
        ) : null}
      </Flex>

      {shouldShowCreateTicketDialog && (
        <IncidentTicketCreateDialog
          open={ticketDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          incidentId={incident.id}
          integration={selectedIntegration}
          envId={selectedEnvId}
          incidentUrl={incidentUrl}
          isCreating={isCreatingTicket}
          error={dialogError}
        />
      )}

      {ticketToUnlink ? (
        <UnlinkIncidentV2TicketDialog
          ticket={ticketToUnlink}
          isUnlinking={isUnlinking}
          onClose={() => setTicketToUnlink(null)}
          onConfirm={unlinkTicket}
        />
      ) : null}
    </Section>
  );
};

export default IncidentV2Tickets;
